import {
  allWayFirst,
  allWayTogether,
  crosswalkStop,
  doNotPass,
  headlights,
  leftTurn,
  roundaboutYield,
  schoolBus,
  stopAtLine,
  warningSign,
  yieldSign,
} from './dmvDiagrams';
import { seedId } from '../lib/contentRecords';
import type { RoadDiagramSpec } from '../lib/roadDiagram';
import type { PracticeQuestion } from '../lib/practiceModel';

/**
 * Course-written practice questions for the California driving path.
 * They are not copied from an official DMV form and they are not a pass guarantee.
 * Rules follow the public driver handbook: all-way stops, crosswalks, yield signs,
 * a full stop, left turns, school buses, roundabouts, and headlights with wipers.
 */
const pathId = seedId(9);

function question(
  id: number,
  topic: string,
  order: number,
  promptEn: string,
  promptMy: string,
  choices: { text_en: string; text_my: string }[],
  correct: number,
  explanationEn: string,
  explanationMy: string,
  tipEn = '',
  tipMy = '',
  image: string | null = null
): PracticeQuestion {
  return {
    id: seedId(id),
    path_id: pathId,
    topic,
    prompt_en: promptEn,
    prompt_my: promptMy,
    choices,
    correct_index: correct,
    explanation_en: explanationEn,
    explanation_my: explanationMy,
    tip_en: tipEn,
    tip_my: tipMy,
    image_url: image,
    diagram: null,
    caption_en: '',
    caption_my: '',
    status: 'published',
    sort_order: order,
  };
}

const VISUALS: Record<number, { diagram: RoadDiagramSpec; caption_en: string; caption_my: string }> = {
  1: {
    diagram: allWayTogether,
    caption_en: 'A and B arrived together. B is on A’s right, so B goes first.',
    caption_my: 'A နဲ့ B တစ်ပြိုင်နက်ရောက်တယ်။ B က A ရဲ့ ညာဘက်မှာမို့ B အရင်သွားတယ်။',
  },
  2: {
    diagram: allWayFirst,
    caption_en: 'A reached the stop first, so A goes before B.',
    caption_my: 'A က အရင်ရောက်လို့ A က B မတိုင်ခင် သွားတယ်။',
  },
  3: {
    diagram: crosswalkStop,
    caption_en: 'A stops before the crosswalk and waits.',
    caption_my: 'A က လူကူးမျဉ်းမတိုင်ခင် ရပ်ပြီး စောင့်တယ်။',
  },
  4: {
    diagram: doNotPass,
    caption_en: 'A has stopped for the crosswalk. B does not pass A.',
    caption_my: 'A က လူကူးအတွက် ရပ်ထားတယ်။ B က A ကို မကျော်ဘူး။',
  },
  5: {
    diagram: yieldSign,
    caption_en: 'Slow down at a yield sign and let the other traffic go.',
    caption_my: 'Yield ဆိုင်းမှာ အရှိန်လျှော့ပြီး တစ်ခြားကားကို လမ်းပေးပါ။',
  },
  6: {
    diagram: stopAtLine,
    caption_en: 'A comes to a full stop behind the line, then looks.',
    caption_my: 'A က မျဉ်းနောက်မှာ အပြည့်ရပ်ပြီးမှ ကြည့်တယ်။',
  },
  7: {
    diagram: warningSign,
    caption_en: 'Most warning signs are yellow diamonds.',
    caption_my: 'သတိပေးဆိုင်း အများစုက အဝါရောင် စိန်ပုံပါ။',
  },
  8: {
    diagram: leftTurn,
    caption_en: 'A is turning left. B is close, so A waits.',
    caption_my: 'A က ဘယ်ကွေ့မယ်။ B က နီးလို့ A စောင့်တယ်။',
  },
  9: {
    diagram: schoolBus,
    caption_en: 'Red lights are flashing and there is no median. B and C stop.',
    caption_my: 'အနီမီးတဖျတ်ဖျတ်၊ ခြားလျှင်မရှိ။ B နဲ့ C ရပ်တယ်။',
  },
  10: {
    diagram: roundaboutYield,
    caption_en: 'B is already in the roundabout. A yields.',
    caption_my: 'B က အဝိုင်းထဲ ရောက်နှင့်ပြီ။ A က လမ်းပေးတယ်။',
  },
  11: {
    diagram: headlights,
    caption_en: 'The wipers are on, so A turns the headlights on.',
    caption_my: 'ရေသုတ်စက် ဖွင့်ထားလို့ A က ရှေ့မီး ဖွင့်တယ်။',
  },
  12: {
    diagram: yieldSign,
    caption_en: 'An upside-down triangle means yield.',
    caption_my: 'ပြောင်းပြန်တြိဂံက yield ပါ။',
  },
};

const PRACTICE_QUESTION_ROWS: PracticeQuestion[] = [
  question(
    501,
    'right-of-way',
    1,
    'Two cars arrive at an all-way stop at the same time. Who goes first?',
    'အဖက်အားလုံးရပ်တဲ့ လမ်းဆုံကို ကားနှစ်စီး တစ်ပြိုင်နက်ရောက်ရင် ဘယ်သူအရင်သွားသလဲ။',
    [
      { text_en: 'The larger car', text_my: 'ကားပိုကြီးတာ' },
      { text_en: 'The car on the right', text_my: 'ညာဘက်က ကား' },
      { text_en: 'The car going straight', text_my: 'တည့်တည့်သွားမည့် ကား' },
      { text_en: 'Whoever honks first', text_my: 'အရင်ဟွန်းတီးသူ' },
    ],
    1,
    'When two vehicles arrive together, yield to the vehicle on your right.',
    'ကားနှစ်စီး တစ်ပြိုင်နက်ရောက်ရင် ညာဘက်က ကားကို လမ်းပေးရပါတယ်။',
    'Look to your right before you move.',
    'မထွက်ခင် ညာဘက်ကို ကြည့်ပါ။'
  ),
  question(
    502,
    'right-of-way',
    2,
    'You reach an all-way stop before the other cars. Who goes first?',
    'အဖက်အားလုံးရပ်တဲ့ လမ်းဆုံကို သင် အရင်ရောက်ရင် ဘယ်သူအရင်သွားသလဲ။',
    [
      { text_en: 'You do', text_my: 'သင် အရင်သွားသည်' },
      { text_en: 'The car on the left', text_my: 'ဘယ်ဘက်က ကား' },
      { text_en: 'Whoever arrived last', text_my: 'နောက်ဆုံးရောက်သူ' },
      { text_en: 'The fastest car', text_my: 'အမြန်ဆုံးကား' },
    ],
    0,
    'At an all-way stop, the first vehicle to arrive goes first. If two arrive together, then yield to the right.',
    'အဖက်အားလုံးရပ်တဲ့နေရာမှာ အရင်ရောက်တဲ့ ကားက အရင်သွားပါတယ်။ တစ်ပြိုင်နက်ရောက်မှ ညာဘက်ကို လမ်းပေးပါ။',
    'Arrival order comes before the yield-to-the-right rule.',
    'အရင်ရောက်သူ စည်းက ညာဘက်လမ်းပေးစည်းထက် အရင်ပါ။'
  ),
  question(
    503,
    'right-of-way',
    3,
    'A pedestrian is in the crosswalk ahead of you. What do you do?',
    'ရှေ့က လူကူးမျဉ်းကျားထဲမှာ လမ်းလျှောက်သူရှိရင် ဘာလုပ်မလဲ။',
    [
      { text_en: 'Stop and wait', text_my: 'ရပ်ပြီး စောင့်ပါ' },
      { text_en: 'Honk and continue', text_my: 'ဟွန်းတီးပြီး ဆက်သွားပါ' },
      { text_en: 'Pass on the left', text_my: 'ဘယ်ဘက်က ကျော်ပါ' },
      { text_en: 'Speed up to clear the crosswalk', text_my: 'အရှိန်တင်ပြီး လူကူးကို ကျော်ပါ' },
    ],
    0,
    'Stop for a pedestrian in a crosswalk. Wait until they have crossed.',
    'လူကူးမျဉ်းကျားထဲက လူအတွက် ရပ်ပါ။ သူကူးပြီးမှ သွားပါ။',
    'People on foot in the crosswalk go before cars.',
    'လူကူးထဲက လမ်းလျှောက်သူက ကားထက် အရင်ပါ။'
  ),
  question(
    504,
    'right-of-way',
    4,
    'The car in front of you has stopped for a pedestrian. What do you do?',
    'ရှေ့က ကားက လမ်းလျှောက်သူအတွက် ရပ်ထားရင် ဘာလုပ်မလဲ။',
    [
      { text_en: 'Do not pass that car', text_my: 'အဲဒီကားကို မကျော်ပါနဲ့' },
      { text_en: 'Pass on the right', text_my: 'ညာဘက်က ကျော်ပါ' },
      { text_en: 'Honk so the car moves', text_my: 'ကားရွှေ့အောင် ဟွန်းတီးပါ' },
      { text_en: 'Drive around through the crosswalk', text_my: 'လူကူးထဲကနေ ရှောင်မောင်းပါ' },
    ],
    0,
    'Do not pass a vehicle that has stopped for a pedestrian.',
    'လမ်းလျှောက်သူအတွက် ရပ်ထားတဲ့ ကားကို မကျော်ပါနဲ့။',
    'A stopped car at a crosswalk is a stop for you too.',
    'လူကူးမှာ ရပ်ထားတဲ့ ကားက သင့်အတွက်လည်း ရပ်ခိုင်းတာပါ။'
  ),
  question(
    505,
    'signs',
    5,
    'What does a yield sign mean?',
    'Yield ဆိုင်းဘုတ်က ဘာကို ဆိုလိုသလဲ။',
    [
      { text_en: 'Slow down and give the road to other traffic', text_my: 'အရှိန်လျှော့ပြီး တစ်ခြားကားကို လမ်းပေးပါ' },
      { text_en: 'Speed up through the intersection', text_my: 'လမ်းဆုံကို အရှိန်တင်ဖြတ်ပါ' },
      { text_en: 'Stop only if a police officer is there', text_my: 'ရဲရှိမှ ရပ်ပါ' },
      { text_en: 'Honk and take the right-of-way', text_my: 'ဟွန်းတီးပြီး လမ်းဦးစားပေးယူပါ' },
    ],
    0,
    'A yield sign means slow down, be ready to stop, and go only when the way is clear.',
    'Yield ဆိုင်းဘုတ်က အရှိန်လျှော့၊ ရပ်ဖို့ အသင့်နေ၊ လမ်းရှင်းမှ သွားပါလို့ ဆိုလိုပါတယ်။',
    'Yield is not a suggestion to keep your speed.',
    'Yield က အရှိန်မလျှော့ဘဲ ဆက်သွားဖို့ မဟုတ်ပါ။',
    '/images/card-yield.svg'
  ),
  question(
    506,
    'signs',
    6,
    'What must you do at a stop sign?',
    'ရပ်ဆိုင်းဘုတ်မှာ ဘာလုပ်ရမလဲ။',
    [
      { text_en: 'Come to a full stop, then look', text_my: 'အပြည့်ရပ်ပြီးမှ ကြည့်ပါ' },
      { text_en: 'Slow to a roll and go', text_my: 'လှိမ့်ပြီး ဆက်သွားပါ' },
      { text_en: 'Stop only when another car is coming', text_my: 'တစ်ခြားကားလာမှ ရပ်ပါ' },
      { text_en: 'Honk and proceed', text_my: 'ဟွန်းတီးပြီး သွားပါ' },
    ],
    0,
    'A stop sign requires a full stop behind the limit line, crosswalk, or intersection. Then look and go when it is safe.',
    'ရပ်ဆိုင်းဘုတ်မှာ မျဉ်း၊ လူကူး၊ သို့မဟုတ် လမ်းဆုံရှေ့ အပြည့်ရပ်ရပါတယ်။ ပြီးမှ ကြည့်ပြီး ဘေးကင်းမှ သွားပါ။',
    'If the wheels are still rolling, you have not stopped.',
    'ဘီးလှိမ့်နေရင် ရပ်ပြီးသေးတာ မဟုတ်ပါ။',
    '/images/card-stop.svg'
  ),
  question(
    507,
    'signs',
    7,
    'Most warning signs are what shape?',
    'သတိပေးဆိုင်းဘုတ် အများစုက ဘယ်လို ပုံသဏ္ဌာန်လဲ။',
    [
      { text_en: 'Diamond', text_my: 'စိန်ပုံ' },
      { text_en: 'Octagon', text_my: 'ရှစ်မြောင့်' },
      { text_en: 'Upside-down triangle', text_my: 'ပြောင်းပြန် တြိဂံ' },
      { text_en: 'Circle', text_my: 'စက်ဝိုင်း' },
    ],
    0,
    'Warning signs are usually yellow and diamond-shaped. A stop sign is an octagon. A yield sign is an upside-down triangle.',
    'သတိပေးဆိုင်းဘုတ်က အဝါရောင် စိန်ပုံ ဖြစ်တတ်ပါတယ်။ ရပ်ဆိုင်းက ရှစ်မြောင့်၊ yield က ပြောင်းပြန်တြိဂံပါ။',
    'Shape tells you the kind of sign before you can read the words.',
    'စာမဖတ်ခင် ပုံသဏ္ဌာန်က ဆိုင်းဘုတ်အမျိုးအစားကို ပြပါတယ်။'
  ),
  question(
    508,
    'sharing-the-road',
    8,
    'You are turning left. An oncoming car is close. What do you do?',
    'ဘယ်ဘက်ကွေ့မယ်။ မျက်နှာချင်းဆိုင်ကားက နီးနေရင် ဘာလုပ်မလဲ။',
    [
      { text_en: 'Wait. A left turn yields to close oncoming traffic', text_my: 'စောင့်ပါ။ ဘယ်ကွေ့က နီးတဲ့ မျက်နှာချင်းဆိုင်ကားကို လမ်းပေးရသည်' },
      { text_en: 'Turn quickly in front of that car', text_my: 'အဲဒီကားရှေ့ အမြန်ကွေ့ပါ' },
      { text_en: 'Honk so the other driver stops', text_my: 'တစ်ဖက်က ရပ်အောင် ဟွန်းတီးပါ' },
      { text_en: 'The left turn always goes first', text_my: 'ဘယ်ကွေ့က အမြဲအရင်သွားသည်' },
    ],
    0,
    'A driver turning left yields to oncoming traffic that is close enough to be a hazard.',
    'ဘယ်ဘက်ကွေ့သူက အန္တရာယ်ဖြစ်လောက်အောင် နီးတဲ့ မျက်နှာချင်းဆိုင်ကားကို လမ်းပေးရပါတယ်။',
    'Wait until the oncoming car has passed or is far enough away.',
    'မျက်နှာချင်းဆိုင်ကား ကျော်သွားမှ သို့မဟုတ် ဝေးမှ ကွေ့ပါ။'
  ),
  question(
    509,
    'sharing-the-road',
    9,
    'A school bus ahead of you has flashing red lights. The road has no median. What do you do?',
    'ရှေ့က ကျောင်းကားက အနီမီးတဖျတ်ဖျတ်လင်းနေသည်။ လမ်းမှာ ခြားလျှင်မရှိပါ။ ဘာလုပ်မလဲ။',
    [
      { text_en: 'Stop from either direction until the red lights stop', text_my: 'အနီမီးမရပ်မချင်း နှစ်ဖက်လုံး ရပ်ပါ' },
      { text_en: 'Pass the bus slowly', text_my: 'ကားကို ဖြည်းဖြည်းကျော်ပါ' },
      { text_en: 'Honk and go around', text_my: 'ဟွန်းတီးပြီး ရှောင်ပါ' },
      { text_en: 'Stop only if you are behind the bus', text_my: 'ကားနောက်မှာ ရှိမှ ရပ်ပါ' },
    ],
    0,
    'Stop for a school bus with flashing red lights from both directions on an undivided road. You do not have to stop when you are on the opposite side of a divided highway.',
    'ခြားမထားတဲ့ လမ်းမှာ ကျောင်းကားအနီမီးတဖျတ်ဖျတ်အတွက် နှစ်ဖက်လုံး ရပ်ရပါတယ်။ ခြားထားတဲ့ အဝေးပြေးလမ်း တစ်ဖက်မှာ ဆိုရင် ရပ်စရာမလိုပါ။',
    'Red lights on a school bus mean children may be crossing.',
    'ကျောင်းကားအနီမီးက ကလေးတွေ ကူးနိုင်တယ်လို့ ဆိုလိုပါတယ်။'
  ),
  question(
    510,
    'sharing-the-road',
    10,
    'You are entering a roundabout. Cars are already going around it. What do you do?',
    'အဝိုင်းလမ်းထဲ ဝင်မယ်။ အထဲမှာ ကားတွေ လည်နေပြီးသား။ ဘာလုပ်မလဲ။',
    [
      { text_en: 'Yield to traffic already in the roundabout', text_my: 'အဝိုင်းထဲမှာ ရှိပြီးသား ကားကို လမ်းပေးပါ' },
      { text_en: 'Enter and make them yield', text_my: 'ဝင်ပြီး သူတို့ကို လမ်းပေးခိုင်းပါ' },
      { text_en: 'Stop in the circle until it is empty', text_my: 'အဝိုင်းထဲမှာ ရပ်ပြီး ရှင်းမှ သွားပါ' },
      { text_en: 'Honk and enter', text_my: 'ဟွန်းတီးပြီး ဝင်ပါ' },
    ],
    0,
    'Yield to traffic already in the roundabout. Enter when there is a safe gap.',
    'အဝိုင်းလမ်းထဲ ရောက်နှင့်ပြီးသား ကားကို လမ်းပေးပါ။ ဟင်းလင်း ဘေးကင်းမှ ဝင်ပါ။',
    'Do not stop once you are inside the circle.',
    'အဝိုင်းထဲ ရောက်ပြီးရင် မရပ်ပါနဲ့။',
    '/images/card-roundabout.svg'
  ),
  question(
    511,
    'sharing-the-road',
    11,
    'Your windshield wipers are in continuous use because of rain. What else do you do?',
    'မိုးရွာလို့ ရှေ့မှန်ရေသုတ်စက်ကို ဆက်တိုက်ဖွင့်ထားရင် နောက်ထပ် ဘာလုပ်မလဲ။',
    [
      { text_en: 'Turn on your headlights', text_my: 'ရှေ့မီး ဖွင့်ပါ' },
      { text_en: 'Use parking lights only', text_my: 'ရပ်မီးပဲ ဖွင့်ပါ' },
      { text_en: 'Honk at each intersection', text_my: 'လမ်းဆုံတိုင်း ဟွန်းတီးပါ' },
      { text_en: 'Nothing. Wipers are enough', text_my: 'ဘာမှမလုပ်ရပါ။ ရေသုတ်စက် လုံလောက်သည်' },
    ],
    0,
    'In California, turn on your headlights when your windshield wipers are in continuous use.',
    'ကယ်လီဖိုးနီးယားမှာ ရှေ့မှန်ရေသုတ်စက်ကို ဆက်တိုက်သုံးရင် ရှေ့မီး ဖွင့်ရပါတယ်။',
    'Headlights help other drivers see you in the rain.',
    'မိုးထဲမှာ တစ်ခြားသူ မြင်အောင် ရှေ့မီးက ကူပါတယ်။'
  ),
  question(
    512,
    'signs',
    12,
    'You see an upside-down triangle sign. What is it telling you?',
    'ပြောင်းပြန်တြိဂံ ဆိုင်းဘုတ်ကို မြင်ရင် ဘာပြောတာလဲ။',
    [
      { text_en: 'Yield', text_my: 'လမ်းပေးပါ' },
      { text_en: 'Stop and wait three seconds', text_my: 'ရပ်ပြီး သုံးစက္ကန့်စောင့်ပါ' },
      { text_en: 'No left turn', text_my: 'ဘယ်မကွေ့ရ' },
      { text_en: 'School zone only on weekends', text_my: 'စနေတနင်္ဂနွေ ကျောင်းဇုန်' },
    ],
    0,
    'An upside-down triangle is a yield sign. Slow down and give the road to cross traffic.',
    'ပြောင်းပြန်တြိဂံက yield ဆိုင်းဘုတ်ပါ။ အရှိန်လျှော့ပြီး ဖြတ်သွားတဲ့ ကားကို လမ်းပေးပါ။',
    '',
    ''
  ),
];

export const PRACTICE_QUESTION_SEED: PracticeQuestion[] = PRACTICE_QUESTION_ROWS.map((item) => {
  const visual = VISUALS[item.sort_order];
  return visual ? { ...item, ...visual } : item;
});
